import React, { useEffect } from 'react';
import {
    Box,
    Modal,
    Typography,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
    Stack,
    InputLabel,
    useMediaQuery,
    CircularProgress,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useResponsiveToast } from '../../../components/Toast';
import { ActionButton } from '../../../components/Button';
import { approvalService } from '../../../services/approvalService';
import { locationService, Ward } from '../../../services/locationService';

interface ExportReportModalProps {
    open: boolean;
    onClose: () => void;
}

const ExportReportModal: React.FC<ExportReportModalProps> = ({ open, onClose }) => {
    const isMobile = useMediaQuery('(max-width:600px)');
    const toast = useResponsiveToast();

    // Status options matching the tabs from ApprovalFilter
    const statusOptions = ['All', 'Not Reviewed', 'Review Passed', 'Fully Approved', 'Review Failed', 'Rejected'];
    
    // Financial years: 5 prior years, then current year through +5 years ahead
    const currentYear = new Date().getFullYear();
    const previousYears = Array.from({ length: 5 }, (_, i) => (currentYear - 5 + i).toString());
    const forwardYears = Array.from({ length: 6 }, (_, i) => (currentYear + i).toString());
    const financialYears = ['All', ...previousYears, ...forwardYears];

    // State for wards data
    const [wards, setWards] = React.useState<Ward[]>([]);
    const [wardsLoading, setWardsLoading] = React.useState(true);

    // State for form values - default to "All" for all filters
    const [selectedStatus, setSelectedStatus] = React.useState('All');
    const [selectedWard, setSelectedWard] = React.useState('All');
    const [selectedYear, setSelectedYear] = React.useState('All');
    const [selectedFormat, setSelectedFormat] = React.useState<'csv' | 'xls' | 'pdf'>('pdf');
    const [isExporting, setIsExporting] = React.useState(false);

    const style = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: '#fff',
        borderRadius: '32px',
        boxShadow: 24,
        px: isMobile ? 3 : 4,
        py: isMobile ? 4 : 5,
        width: isMobile ? '80%' : 500,
    };

    const selectBoxSx = {
        borderRadius: '12px',
        width: 140,
        height: 48,
    };

    // Load wards data on component mount
    useEffect(() => {
        if (open) {
            const fetchWards = async () => {
                try {
                    setWardsLoading(true);
                    const wardsData = await locationService.getWards();
                    setWards(wardsData);
                    // Keep "All" as default selection - no need to set first ward
                } catch (err) {
                    console.error('Error fetching wards:', err);
                    toast.error('Failed to load wards data');
                } finally {
                    setWardsLoading(false);
                }
            };
            
            fetchWards();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]); // Only depend on 'open' to avoid infinite loop

    const handleExport = async () => {
        try {
            setIsExporting(true);
            
            // Build export parameters - only include non-"All" selections
            const exportParams: any = {
                format: selectedFormat
            };
            
            // Only add ward_no if not "All"
            if (selectedWard !== 'All') {
                exportParams.ward_no = selectedWard;
            }
            
            // Only add status filters if not "All"
            if (selectedStatus !== 'All') {
                exportParams.approved = selectedStatus === 'Fully Approved';
                exportParams.review_passed = selectedStatus === 'Review Passed';
            }
            
            // Only add financial_year if not "All"
            if (selectedYear !== 'All') {
                exportParams.financial_year = selectedYear;
            }
            
            await approvalService.exportApprovals(exportParams);
            
            // Close modal after successful export
            onClose();
        } catch (error: any) {
            console.error('Export failed:', error);
            
            // Handle specific 404 error for no records found
            // Check if this is an AxiosError with response data (preserved by interceptor)
            if (error.response && error.response.data) {
                // For blob responses, we need to read the data as text to get the error message
                if (error.response.data instanceof Blob) {
                    try {
                        const text = await error.response.data.text();
                        const errorData = JSON.parse(text);
                        if (errorData.detail && errorData.detail.includes('No records found for export')) {
                            toast.error('No records found for the selected criteria. Please try different filters.');
                            return;
                        }
                    } catch (parseError) {
                        console.error('Error parsing blob response:', parseError);
                    }
                } else {
                    // For regular JSON responses
                    if (error.response.data.detail && error.response.data.detail.includes('No records found for export')) {
                        toast.error('No records found for the selected criteria. Please try different filters.');
                        return;
                    }
                }
            }
            
            // Handle other errors
            toast.error('Export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Stack spacing={isMobile ? 3 : 4}>
                    {/* Title */}
                    <Stack direction='row' justifyContent='center' alignItems='center' spacing={1}>
                        <FileUploadOutlinedIcon sx={{ color: '#132F5F', fontSize: { xs: 24, sm: 28 } }} />
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontWeight={600}
                            fontSize={{ xs: '18px', sm: '20px' }}
                            lineHeight='28px'
                            color='#132F5F'
                        >
                            Export Report
                        </Typography>
                    </Stack>

                    {/* Row 1 */}
{isMobile ? (
    <>
        <Stack direction='row' spacing={2}>
            <Box>
                <InputLabel sx={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                    Status
                </InputLabel>
                <Select 
                    fullWidth 
                    size='small' 
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    sx={selectBoxSx}
                >
                    {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                </Select>
            </Box>
            <Box>
                <InputLabel sx={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                    Ward Number
                </InputLabel>
                <Select 
                    fullWidth 
                    size='small' 
                    value={selectedWard}
                    onChange={(e) => setSelectedWard(e.target.value)}
                    disabled={wardsLoading}
                    sx={selectBoxSx}
                >
                    <MenuItem value="All">All</MenuItem>
                    {wards.map((ward) => (
                        <MenuItem key={ward.id} value={ward.ward_number}>{ward.ward_number}</MenuItem>
                    ))}
                </Select>
            </Box>
        </Stack>
        <Box mt={2}>
            <InputLabel sx={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                Financial Year
            </InputLabel>
            <Select 
                fullWidth 
                size='small' 
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                sx={selectBoxSx}
            >
                {financialYears.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
            </Select>
        </Box>
    </>
) : (
    <Stack direction='row' spacing={3}>
        <Box>
            <InputLabel sx={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                Status
            </InputLabel>
            <Select 
                fullWidth 
                size='small' 
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                sx={selectBoxSx}
            >
                {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
            </Select>
        </Box>
        <Box>
            <InputLabel sx={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                Ward Number
            </InputLabel>
            <Select 
                fullWidth 
                size='small' 
                value={selectedWard}
                onChange={(e) => setSelectedWard(e.target.value)}
                disabled={wardsLoading}
                sx={selectBoxSx}
            >
                <MenuItem value="All">All</MenuItem>
                {wards.map((ward) => (
                    <MenuItem key={ward.id} value={ward.ward_number}>{ward.ward_number}</MenuItem>
                ))}
            </Select>
        </Box>
        <Box>
            <InputLabel sx={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                Financial Year
            </InputLabel>
            <Select 
                fullWidth 
                size='small' 
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                sx={selectBoxSx}
            >
                {financialYears.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
            </Select>
        </Box>
    </Stack>
)}

                    {/* Export Format */}
                    <Box>
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontSize={{ xs: '13px', sm: '14px' }}
                            fontWeight={500}
                            sx={{ mb: 1, ml: 1 }}
                        >
                            Export Format
                        </Typography>
                        <RadioGroup 
                            value={selectedFormat}
                            onChange={(e) => setSelectedFormat(e.target.value as 'csv' | 'xls' | 'pdf')}
                        >
                            <Stack direction='row' spacing={isMobile ? 3 : 4} alignItems='center' ml={1}>
                                <FormControlLabel
                                    value='pdf'
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#132F5F',
                                                '&.Mui-checked': { color: '#132F5F' },
                                            }}
                                        />
                                    }
                                    label='pdf'
                                    labelPlacement={isMobile ? 'end' : 'start'}
                                    sx={{ 
                                        fontFamily: 'Poppins, sans-serif', 
                                        ml: isMobile ? 0 : 1,
                                        fontSize: { xs: 13, sm: 14 }
                                    }}
                                />
                                <FormControlLabel
                                    value='xls'
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#132F5F',
                                                '&.Mui-checked': { color: '#132F5F' },
                                            }}
                                        />
                                    }
                                    label='xls'
                                    labelPlacement={isMobile ? 'end' : 'start'}
                                    sx={{ 
                                        fontFamily: 'Poppins, sans-serif',
                                        fontSize: { xs: 13, sm: 14 }
                                    }}
                                />
                                <FormControlLabel
                                    value='csv'
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#132F5F',
                                                '&.Mui-checked': { color: '#132F5F' },
                                            }}
                                        />
                                    }
                                    label='csv'
                                    labelPlacement={isMobile ? 'end' : 'start'}
                                    sx={{ 
                                        fontFamily: 'Poppins, sans-serif',
                                        fontSize: { xs: 13, sm: 14 }
                                    }}
                                />
                            </Stack>
                        </RadioGroup>
                    </Box>

                    {/* Export Button */}
                    <Box display='flex' justifyContent='center' mt={1}>
                        <ActionButton
                            startIcon={
                                isExporting ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <FileUploadOutlinedIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                )
                            }
                            onClick={handleExport}
                            disabled={isExporting}
                            width="100%"
                            sx={{
                                height: { xs: 48, sm: 56 },
                                fontSize: { xs: 14, sm: 16 },
                                lineHeight: '24px',
                                fontWeight: 500,
                                px: { xs: 3, sm: 4 },
                                maxWidth: { sm: 128 },
                                '&:disabled': {
                                    backgroundColor: '#ccc',
                                },
                            }}
                        >
                            {isExporting ? 'Exporting...' : 'Export'}
                        </ActionButton>
                    </Box>
                </Stack>
            </Box>
        </Modal>
    );
};

export default ExportReportModal;
